<template>
  <v-dialog v-model="dialog" persistent width="1000">
    <v-card class="institution-cars-root">
      <v-card-title class="redColor text-white">
        <span class="headline">{{ $t("institutions.cars.label") }}</span>
      </v-card-title>
      <v-card-text>
        <v-row v-if="loading" class="ma-4" justify-center>
          <v-progress-circular :size="100" color="red-darken-2" indeterminate />
        </v-row>

        <v-row v-if="!loading" row wrap>
          <!--ALERT-->
          <v-col xs="12" md="12" lg="12">
            <v-alert
              :model-value="alert.value"
              :type="alert.type"
              variant="tonal"
              border="bottom"
            >
              {{ alert.message }}
            </v-alert>
          </v-col>

          <!--SELECTED CARS-->
          <v-col xs="12" md="12" lg="12" class="mb-4">
            <v-chip
              v-for="(item, index) in cars"
              :key="index"
              closable
              class="ma-1"
              @input="removeCar(index)"
            >
              {{ item.brand.title }} {{ item.model.description }}
              {{ item.version.description }}
            </v-chip>
          </v-col>
          <!--BRAND-->
          <v-col cols="12" xs="12" sm="6">
            <v-autocomplete
              v-model="car.brand"
              :items="brands"
              :label="$t('parts.advanced_search.brand')"
              item-title="title"
              variant="solo"
              color="red-darken-1"
              hide-details
              return-object
              clearable
            >
              <!-- Custom dropdown item -->
              <template #item="{ props, item }">
                <v-list-item v-bind="props">
                  <template #prepend>
                    <v-img
                      :src="item.raw.logo"
                      height="35"
                      width="35"
                      class="mr-3"
                      cover
                    />
                  </template>
                  <!-- <v-list-item-title>{{ item.raw.title }}</v-list-item-title> -->
                </v-list-item>
              </template>

              <!-- Selected value in the field -->
              <template #selection="{ item }">
                <div class="d-flex align-center">
                  <v-img
                    :src="item.raw.logo"
                    height="35"
                    width="35"
                    class="mr-3"
                    cover
                  />
                  <span>{{ item.raw.title }}</span>
                </div>
              </template>
            </v-autocomplete>
          </v-col>
          <!--MODEL-->
          <v-col cols="12" xs="12" sm="6">
            <v-combobox
              v-model="car.model"
              :items="models"
              :label="$t('parts.advanced_search.model')"
              item-title="description"
              item-value="modelCode"
              variant="solo"
              color="red-darken-1"
              hide-details
              return-object
              clearable
              class="car-search-combobox"
            >
              <!-- Dropdown items -->
              <template #item="{ props, item }">
                <v-list-item v-bind="props">
                  <v-list-item-content>
                    <v-row>
                      <!-- Description -->
                      <v-col cols="12" md="4" class="bg-grey-lighten-3 mt-2">
                        <div class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.model")
                          }}</span>
                          <span class="font-weight-bold">{{
                            item.raw.description
                          }}</span>
                        </div>
                      </v-col>

                      <!-- Year range -->
                      <v-col
                        cols="12"
                        md="8"
                        class="bg-grey-lighten-3 mt-2"
                      >
                        <div class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.year")
                          }}</span>
                          <span class="font-weight-bold">
                            {{ formatMonthYear(item.raw.startingDate) }} -
                            {{ formatMonthYear(item.raw.endingDate) }}
                          </span>
                        </div>
                      </v-col>
                    </v-row>
                  </v-list-item-content>
                </v-list-item>
              </template>

              <!-- Selected value -->
              <template #selection="{ item }">
                <div
                  class="car-detail-selection d-flex justify-between align-center pa-2 w-100"
                >
                  <div class="car-detail-col d-flex flex-column pr-2">
                    <span class="caption">{{
                      $t("parts.advanced_search.model")
                    }}</span>
                    <span class="font-weight-bold">{{
                      item.raw.description
                    }}</span>
                  </div>
                  <div class="car-detail-col d-flex flex-column">
                    <span class="caption">{{
                      $t("parts.advanced_search.year")
                    }}</span>
                    <span class="font-weight-bold">
                      {{ formatMonthYear(item.raw.startingDate) }} -
                      {{ formatMonthYear(item.raw.endingDate) }}
                    </span>
                  </div>
                </div>
              </template>
            </v-combobox>
          </v-col>
          <!--VERSION-->
          <v-col cols="12" xs="12" md="12" lg="12">
            <v-combobox
              v-model="car.versions"
              :items="versions"
              chips
              closable-chips
              multiple
              :label="$t('parts.advanced_search.version')"
              item-title="fullDescription"
              item-value="versionCode"
              variant="solo"
              color="red-darken-1"
              hide-details
              return-object
              clearable
              class="car-search-combobox car-search-version-combobox"
            >
              <!-- Dropdown items -->
              <template #item="{ item, props }">
                <v-list-item v-bind="props">
                  <div
                    class="d-sm-none car-version-detail-item bg-grey-lighten-3 mt-2 pa-3 w-100"
                  >
                    <div
                      class="car-version-detail-row car-version-detail-row--dropdown"
                    >
                      <div
                        class="car-version-detail-col car-version-detail-col--main"
                      >
                        <span class="caption">{{
                          $t("parts.advanced_search.version")
                        }}</span>&nbsp;
                        <span class="font-weight-bold">{{
                          item.raw.description
                        }}</span>
                      </div>
                      <div class="car-version-detail-col">
                        <span class="caption">{{
                          $t("parts.advanced_search.year")
                        }}</span>&nbsp;
                        <span class="font-weight-bold">
                          {{ formatMonthYear(item.raw.startingDate) }} -
                          {{ formatMonthYear(item.raw.endingDate) }}
                        </span>
                      </div>
                      <div class="car-version-detail-col">
                        <span class="caption">{{
                          $t("parts.advanced_search.body")
                        }}</span>&nbsp;
                        <span class="font-weight-bold">{{
                          item.raw.numberOfDoors
                        }}</span>
                      </div>
                      <div class="car-version-detail-col">
                        <span class="caption">{{
                          $t("parts.advanced_search.power")
                        }}</span>&nbsp;
                        <span class="font-weight-bold">
                          {{ item.raw.power ? item.raw.power.kw : "" }} kw,
                        </span>
                      </div>
                      <div class="car-version-detail-col">
                        <span class="caption">{{
                          $t("parts.advanced_search.engine")
                        }}</span>&nbsp;
                        <span class="font-weight-bold">{{
                          item.raw.engineCode
                        }}</span>
                      </div>
                      <div class="car-version-detail-col">
                        <span class="caption">{{
                          $t("parts.advanced_search.supply.supply")
                        }}</span>&nbsp;
                        <span class="font-weight-bold">{{
                          getSupplyLocale(item.raw.supplyCode)
                        }}</span>
                      </div>
                    </div>
                  </div>
                    <v-row class="d-none d-sm-flex pa-3 bg-grey-lighten-3 mt-2">
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.version")
                          }}</span>
                          <span class="font-weight-bold">{{
                            item.raw.description
                          }}</span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.year")
                          }}</span>
                          <span class="font-weight-bold">
                            {{ formatMonthYear(item.raw.startingDate) }} -
                            {{ formatMonthYear(item.raw.endingDate) }}
                          </span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.body")
                          }}</span>
                          <span class="font-weight-bold">{{
                            item.raw.numberOfDoors
                          }}</span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.power")
                          }}</span>
                          <span class="font-weight-bold">
                            {{ item.raw.power ? item.raw.power.kw : "" }} kw,
                          </span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.engine")
                          }}</span>
                          <span class="font-weight-bold">{{
                            item.raw.engineCode
                          }}</span>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row class="d-flex flex-column">
                          <span class="caption">{{
                            $t("parts.advanced_search.supply.supply")
                          }}</span>
                          <span class="font-weight-bold">{{
                            getSupplyLocale(item.raw.supplyCode)
                          }}</span>
                        </v-row>
                      </v-col>
                    </v-row>
                </v-list-item>
              </template>
            </v-combobox>
          </v-col>
          <v-col xs="12" md="12" lg="12">
            <!-- Mobile: stacked full-width buttons -->
            <div class="d-flex d-sm-none flex-column ga-2">
              <v-btn
                :disabled="!car.brand || !car.model || versions.length < 1"
                color="primary"
                variant="flat"
                block
                @click="addAllVersions"
              >
                {{ $t("institutions.cars.add_all_versions") }}
                <v-icon end> mdi-expand-all-outline </v-icon>
              </v-btn>
              <v-btn
                :disabled="
                  !car.brand ||
                  !car.model ||
                  !car.versions ||
                  car.versions.length < 1
                "
                color="success"
                variant="flat"
                block
                @click="addCar"
              >
                {{ $t("institutions.cars.add_car") }}
                <v-icon end> mdi-car </v-icon>
              </v-btn>
            </div>

            <!-- Desktop: unchanged inline buttons -->
            <v-row class="justify-end d-none d-sm-flex">
              <v-btn
                :disabled="!car.brand || !car.model || versions.length < 1"
                color="primary"
                variant="flat"
                class="mx-1"
                @click="addAllVersions"
              >
                {{ $t("institutions.cars.add_all_versions") }}
                <v-icon end> mdi-expand-all-outline </v-icon>
              </v-btn>
              <v-btn
                :disabled="
                  !car.brand ||
                  !car.model ||
                  !car.versions ||
                  car.versions.length < 1
                "
                color="success"
                variant="flat"
                @click="addCar"
              >
                {{ $t("institutions.cars.add_car") }}
                <v-icon end> mdi-car </v-icon>
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="red-darken-1" variant="outlined" @click="close">
          {{ $t("institutions.form.close") }}
        </v-btn>
        <v-btn
          :loading="loadingSave"
          color="green-darken-1"
          variant="outlined"
          @click="save"
        >
          {{ $t("institutions.form.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";

import getRequest from "../../api/getRequest";
import putRequest from "../../api/putRequest";

export default {
  filters: {
    moment(date) {
      moment.locale(localStorage.getItem("language"));
      const valid = moment(date).isValid();
      if (valid) return moment(date).format("MM/YYYY");
      return "";
    },
  },
  data: () => ({
    dialog: false,
    loading: false,
    loadingSave: false,
    loadingBrand: false,
    loadingModel: false,
    loadingVersion: false,
    institutionId: null,
    brands: [],
    models: [],
    versions: [],
    car: {
      brand: null,
      model: null,
      versions: [],
    },
    cars: [],
    alert: {
      value: false,
      type: "info",
      message: "",
    },
  }),
  watch: {
    "car.brand": {
      handler(value) {
        if (value) {
          this.models = [];
          this.model = null;
          this.versions = [];
          this.version = null;
          this.getModels(value.brandCode);
        }
      },
    },
    "car.model": {
      handler(value) {
        if (value) {
          this.versions = [];
          this.version = null;
          this.getVersions(value.brandCode, value.modelCode);
        }
      },
    },
  },
  methods: {
    open(id) {
      this.dialog = true;
      this.loading = true;
      this.institutionId = id;
      getRequest("/institution/cars", `institution_id=${this.institutionId}`)
        .then((response) => {
          this.cars = response.cars;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
      this.getBrands();
    },
    close() {
      this.dialog = false;
      this.loading = false;
      this.institutionId = null;
      this.clearCars();
      this.cars = [];
    },
    save() {
      this.loadingSave = true;
      const cars = this.cars.map((car) => ({
        brand: car.brand._id,
        model: car.model._id,
        version: car.version._id,
      }));

      putRequest("/institution/cars", `institution_id=${this.institutionId}`, {
        cars,
      })
        .then(() => {
          this.close();
        })
        .catch(() => {
          this.alert = {
            value: true,
            type: "error",
            message: this.$t("alert"),
          };
          setTimeout(() => {
            this.alert.value = false;
          }, 5000);
        })
        .finally(() => {
          this.loadingSave = false;
        });
    },
    addAllVersions() {
      this.car.versions = [];
      this.versions.forEach((v) => this.car.versions.push(v));
    },
    addCar() {
      const selectedCars = this.car.versions.map((version) => ({
        brand: this.car.brand,
        model: this.car.model,
        version,
      }));
      // Check if car is already selected to avoid duplications.
      const cars = selectedCars.reduce((accumulator, current) => {
        const found = this.cars.find(
          (car) =>
            car.brand._id === current.brand._id &&
            car.model._id === current.model._id &&
            car.version._id === current.version._id,
        );
        if (!found) accumulator.push(current);
        return accumulator;
      }, []);

      this.cars = this.cars.concat(cars);
      this.clearCars();
    },
    removeCar(index) {
      this.cars.splice(index, 1);
    },
    clearCars() {
      this.car = { brand: null, model: null, versions: [] };
    },
    removeSelectedVersion(version) {
      const index = this.car.versions.findIndex(
        (v) => v._id.toString() === version._id.toString(),
      );
      this.car.versions.splice(index, 1);
    },

    getBrands() {
      this.loadingBrand = true;
      getRequest("/info-car/brands")
        .then((response) => {
          this.brands = response.brands;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingBrand = false;
        });
    },
    getModels(brandCode) {
      this.loadingModel = true;
      getRequest("/info-car/models", `brand_code=${brandCode}`)
        .then((response) => {
          this.models = response.models;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingModel = false;
        });
    },
    getVersions(brandCode, modelCode) {
      this.loadingVersion = true;
      getRequest(
        "/info-car/versions",
        `brand_code=${brandCode}&model_code=${modelCode}`,
      )
        .then((response) => {
          this.versions = response.versions;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingVersion = false;
        });
    },

    getSupplyLocale(supply) {
      switch (supply) {
        case "B":
          return this.$t("parts.advanced_search.supply.petrol");
        case "D":
          return this.$t("parts.advanced_search.supply.diesel");
        case "G":
          return this.$t("parts.advanced_search.supply.gas");
        default:
          return supply;
      }
    },
    formatMonthYear(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // months are 0-indexed
      const year = date.getUTCFullYear();
      return `${month}/${year}`;
    },
  },
};
</script>

<style scoped>
/* Model/version combobox mobile layout */
@media (max-width: 959px) {
  .institution-cars-root .car-version-detail-selection {
    width: 100%;
    padding: 0.375rem 0.25rem;
  }

  .institution-cars-root .car-version-detail-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    width: 100%;
    gap: 0.75rem 1rem;
  }

  .institution-cars-root .car-version-detail-col {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
    flex: 1 1 0;
  }

  .institution-cars-root .car-version-detail-col .caption {
    line-height: 1.25;
    margin-bottom: 0.125rem;
  }

  .institution-cars-root .car-version-detail-col .font-weight-bold {
    line-height: 1.4;
    word-break: break-word;
  }

  .institution-cars-root .car-search-combobox :deep(.v-field) {
    --v-field-padding-top: 10px;
    --v-field-padding-bottom: 10px;
    height: auto !important;
    min-height: 3.5rem;
  }

  .institution-cars-root .car-search-version-combobox :deep(.v-field) {
    --v-field-padding-top: 12px;
    --v-field-padding-bottom: 12px;
    min-height: 4.5rem;
  }

  .institution-cars-root .car-search-combobox :deep(.v-field__field) {
    height: auto !important;
    min-height: 3.5rem;
    align-items: flex-start;
  }

  .institution-cars-root .car-search-version-combobox :deep(.v-field__field) {
    min-height: 4.5rem;
  }

  .institution-cars-root .car-search-combobox :deep(.v-field__input) {
    white-space: normal;
    overflow: visible;
    align-items: flex-start;
    flex-wrap: wrap;
    min-height: unset;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .institution-cars-root .car-search-version-combobox :deep(.v-field__input) {
    display: flex !important;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem;
    height: auto !important;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .institution-cars-root .car-search-combobox :deep(.v-combobox__selection) {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    width: 100%;
    max-width: 100%;
  }

  .institution-cars-root .car-search-version-combobox :deep(.v-combobox__selection) {
    display: block !important;
    width: 100%;
    position: static !important;
    transform: none !important;
  }

  .institution-cars-root .car-search-version-combobox :deep(.v-chip) {
    margin: 0.125rem 0.25rem 0.125rem 0;
    max-width: 100%;
  }

  .institution-cars-root .car-detail-selection {
    overflow: visible;
    width: 100%;
  }


  .institution-cars-root .car-version-detail-row,
  .institution-cars-root .car-version-detail-row--dropdown {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem 0.875rem;
    flex-wrap: unset;
  }

  .institution-cars-root .car-version-detail-col {
    flex: unset;
    padding: 0;
  }

  .institution-cars-root .car-version-detail-col--main {
    grid-column: 1 / -1;
    padding-bottom: 0.625rem;
    margin-bottom: 0.25rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .institution-cars-root .car-version-detail-col--main .font-weight-bold {
    line-height: 1.45;
  }

  .institution-cars-root .car-detail-col {
    flex: 1 1 calc(50% - 0.5rem);
    max-width: none;
    min-width: 0;
    padding: 0;
    word-break: break-word;
  }

  .institution-cars-root .car-detail-selection.d-flex {
    flex-wrap: wrap;
    gap: 0.625rem 0.75rem;
  }
}
</style>
